/**
 * JAGS LUXURY COLLECTION CONTROLLER
 * Supports: Rounded pill filter, centered title layouts, custom grid persistent columns,
 * glassmorphic quick-buy sizes, and isolated filtering/sorting handlers.
 */

document.addEventListener('DOMContentLoaded', () => {
  initJagsCollectionPage();
});

// Shopify dynamic section reload listener
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.jags-collection-section')) {
    initJagsCollectionPage();
  }
});

function initJagsCollectionPage() {
  const sections = document.querySelectorAll('.jags-collection-section');
  if (!sections.length) return;

  sections.forEach((section) => {
    // Sync theme mode directly from header-section
    const headerSection = document.querySelector('header-section');
    if (headerSection) {
      const syncTheme = () => {
        const themeMode = headerSection.getAttribute('data-theme-mode') || 'auto';
        let isDark = false;
        if (themeMode === 'dark') {
          isDark = true;
        } else if (themeMode === 'light') {
          isDark = false;
        } else {
          // Auto Mode (System Preferred)
          isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        }

        const drawers = section.querySelectorAll('.jags-filter-drawer');
        drawers.forEach((drawer) => {
          const headerStyles = window.getComputedStyle(headerSection);
          const variables = [
            '--glass-bg',
            '--glass-border',
            '--glass-text',
            '--glass-text-hover',
            '--glass-blur',
            '--glass-shadow',
            '--glass-accent',
            '--glass-divider',
            '--glass-inset'
          ];
          variables.forEach((variable) => {
            const val = headerStyles.getPropertyValue(variable);
            if (val) {
              drawer.style.setProperty(variable, val.trim());
            }
          });

          if (isDark) {
            drawer.classList.add('dark-theme-active');
          } else {
            drawer.classList.remove('dark-theme-active');
          }
        });

        const overlays = section.querySelectorAll('.jags-quick-add-overlay');
        overlays.forEach((overlay) => {
          if (isDark) {
            overlay.classList.add('dark-theme-active');
          } else {
            overlay.classList.remove('dark-theme-active');
          }
        });
      };

      syncTheme();

      const observer = new MutationObserver(syncTheme);
      observer.observe(headerSection, { attributes: true, attributeFilter: ['data-theme-mode'] });

      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      if (mq.addEventListener) {
        mq.addEventListener('change', syncTheme);
      }
    }

    const sectionId = section.getAttribute('data-section-id');
    const filterTrigger = section.querySelector('.jags-filter-trigger-btn');
    const closeBtn = document.querySelector(`#JagsFilterDrawer-${sectionId} .jags-drawer-close-btn`);
    const backdrop = document.querySelector(`#JagsFilterBackdrop-${sectionId}`);
    const drawer = document.querySelector(`#JagsFilterDrawer-${sectionId}`);
    
    // Grid items & empty state elements
    const grid = section.querySelector(`#JagsProductGrid-${sectionId}`);
    if (!grid) return;

    const activeFiltersRow = section.querySelector(`#JagsActiveFilters-${sectionId}`);
    const filterForm = document.querySelector(`#JagsFiltersForm-${sectionId}`);

    // 1. Drawer Navigation Trigger
    function openRefineDrawer() {
      if (drawer && backdrop) {
        document.body.style.overflow = 'hidden';
        backdrop.classList.add('is-open');
        drawer.classList.add('is-open');
      }
    }

    function closeRefineDrawer() {
      if (drawer && backdrop) {
        document.body.style.overflow = '';
        backdrop.classList.remove('is-open');
        drawer.classList.remove('is-open');
      }
    }

    if (filterTrigger) {
      filterTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openRefineDrawer();
      });
    }
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeRefineDrawer();
      });
    }
    if (backdrop) {
      backdrop.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeRefineDrawer();
      });
    }

    // Capture clicks globally to close drawer if clicked completely outside
    document.addEventListener('click', (e) => {
      if (drawer && drawer.classList.contains('is-open')) {
        if (!drawer.contains(e.target) && (!filterTrigger || !filterTrigger.contains(e.target))) {
          closeRefineDrawer();
        }
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeRefineDrawer();
      }
    });

    // 2. Persistent Custom Grid Sizers Toggle
    const gridBtns = section.querySelectorAll('.jags-grid-btn');
    
    function applyGridColumns() {
      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      let selectedCols;
      if (isDesktop) {
        selectedCols = localStorage.getItem('jags-cols-desktop') || '5';
      } else {
        selectedCols = localStorage.getItem('jags-cols-mobile') || '2';
      }

      // Safeguard values to ensure they correspond to requested options
      if (isDesktop) {
        if (selectedCols !== '3' && selectedCols !== '4' && selectedCols !== '5') {
          selectedCols = '5';
        }
      } else {
        if (selectedCols !== '1' && selectedCols !== '2' && selectedCols !== '3') {
          selectedCols = '2';
        }
      }

      // Apply columns class to master grid
      grid.className = 'jags-grid-layout';
      grid.classList.add(`cols-${selectedCols}`);

      // Sync active button state
      gridBtns.forEach(btn => {
        const btnCols = btn.getAttribute('data-grid-cols');
        const isDesktopBtn = btn.closest('.jags-grid-switcher-desktop') !== null;
        if (btnCols === selectedCols) {
          if ((isDesktop && isDesktopBtn) || (!isDesktop && !isDesktopBtn)) {
            btn.classList.add('active');
          } else {
            btn.classList.remove('active');
          }
        } else {
          btn.classList.remove('active');
        }
      });
    }

    // Set up button listeners
    gridBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const selectedCols = btn.getAttribute('data-grid-cols');
        const isDesktop = window.matchMedia('(min-width: 750px)').matches;
        
        if (isDesktop) {
          localStorage.setItem('jags-cols-desktop', selectedCols);
        } else {
          localStorage.setItem('jags-cols-mobile', selectedCols);
        }
        
        applyGridColumns();
      });
    });

    // Run on initial load
    applyGridColumns();

    // Listen for screen size changes
    const mql = window.matchMedia('(min-width: 750px)');
    if (mql.addEventListener) {
      mql.addEventListener('change', applyGridColumns);
    } else if (mql.addListener) {
      mql.addListener(applyGridColumns);
    }

    // 3. Size Picker Hover - Dynamic Add to Cart Integrator
    function initQuickAddSizes(containerElement) {
      const cardsInGrid = containerElement.querySelectorAll('.jags-card-item');
      cardsInGrid.forEach((card) => {
        const sizeBtns = card.querySelectorAll('.jags-size-btn');
        sizeBtns.forEach((btn) => {
          if (btn.dataset.bound) return;
          btn.dataset.bound = 'true';

          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            if (btn.classList.contains('adding')) return;
            btn.classList.add('adding');
            
            const oldText = btn.textContent;
            btn.textContent = '...';
            btn.style.opacity = '0.5';
            
            const variantId = btn.getAttribute('data-variant-id');
            if (!variantId) return;

            // Retrieve card metadata
            const titleEl = card.querySelector('.jags-product-title a') || card.querySelector('.jags-product-title');
            const titleText = titleEl ? titleEl.textContent.trim() : 'Product';

            const priceEl = card.querySelector('.jags-product-price');
            const priceText = priceEl ? priceEl.textContent.trim() : '';

            const imgEl = card.querySelector('.jags-product-card__image--primary');
            let imgSrc = imgEl ? imgEl.getAttribute('src') : '';
            if (imgSrc && imgSrc.startsWith('//')) {
              imgSrc = 'https:' + imgSrc;
            }

            const colorEl = card.querySelector('.jags-product-colorway');
            const colorText = colorEl ? colorEl.textContent.trim() : 'Signature Black';

            const sizeText = btn.textContent.trim();

            // Construct form data for Shopify Cart API
            const addUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add.js';
            const formData = new FormData();
            formData.append('id', variantId);
            formData.append('quantity', '1');

            const cartElement = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
            if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
              const sectionsToRender = cartElement.getSectionsToRender();
              if (sectionsToRender && Array.isArray(sectionsToRender)) {
                formData.append('sections', sectionsToRender.map((section) => section.id).join(','));
              }
              formData.append('sections_url', window.location.pathname);
            }

            fetch(addUrl, {
              method: 'POST',
              body: formData,
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json'
              }
            })
            .then(res => {
              if (!res.ok) throw new Error('Failed to update Shopify cart');
              return res.json();
            })
            .then(data => {
              return fetch(window.Shopify && window.Shopify.routes ? `${window.Shopify.routes.root}cart.js` : '/cart.js')
                .then(r => r.json())
                .then(cartData => {
                  btn.classList.remove('adding');
                  btn.style.opacity = '';
                  btn.textContent = 'ADDED';
                  
                  setTimeout(() => {
                    btn.textContent = oldText;
                  }, 1500);

                  document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cartData } }));
                  document.dispatchEvent(new CustomEvent('wearjags-cart-updated', { bubbles: true }));
                  
                  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: variantId, cartData: cartData });
                  }

                  if (window.ShopifyCart && typeof window.ShopifyCart.refresh === 'function') {
                    window.ShopifyCart.refresh();
                  } else {
                    const cartDrawer = document.querySelector('cart-drawer');
                    if (cartDrawer && typeof cartDrawer.open === 'function') {
                      cartDrawer.open();
                    }
                  }
                });
            })
            .catch(err => {
              console.error('Core addition failure:', err);
              btn.classList.remove('adding');
              btn.style.opacity = '';
              btn.textContent = 'ERR';
              setTimeout(() => {
                btn.textContent = oldText;
              }, 1500);
            });
          });
        });
      });
    }

    // 4. Shopify Storefront dynamic AJAX filtering & URL sync
    function applyStorefrontFilters() {
      if (!filterForm) return;

      const formData = new FormData(filterForm);
      formData.delete('jags-collection-redirect');
      const searchParams = new URLSearchParams(formData).toString();
      const targetUrl = `${window.location.pathname}?${searchParams}`;

      // Visual feedback loading state
      const gridContainer = section.querySelector('.jags-products-grid-container');
      if (gridContainer) {
        gridContainer.style.opacity = '0.5';
        gridContainer.style.pointerEvents = 'none';
      }

      fetch(targetUrl)
        .then(response => {
          if (!response.ok) throw new Error('Storefront filter request failed.');
          return response.text();
        })
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          // Update product grid dynamically
          const newGrid = doc.querySelector(`#JagsProductGrid-${sectionId}`);
          const oldGrid = section.querySelector(`#JagsProductGrid-${sectionId}`);
          if (newGrid && oldGrid) {
            oldGrid.innerHTML = newGrid.innerHTML;
            
            // Stagger animation trigger
            const newCards = Array.from(oldGrid.querySelectorAll('.jags-card-item'));
            newCards.forEach((card, idx) => {
              card.style.animation = 'none';
              void card.offsetWidth; // Force reflux
              card.style.animation = 'cardFadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards';
              card.style.animationDelay = `${idx * 40}ms`;
            });

            // Re-initialize hover sizes selection listeners (since cards are newly rendered)
            initQuickAddSizes(oldGrid);
          }

          // Sync URL search parameters
          history.pushState({}, '', targetUrl);

          // Update active dynamic badges row
          renderActiveBadges(formData);

          if (gridContainer) {
            gridContainer.style.opacity = '1';
            gridContainer.style.pointerEvents = '';
          }
        })
        .catch(error => {
          console.error('Filtering error:', error);
          if (gridContainer) {
            gridContainer.style.opacity = '1';
            gridContainer.style.pointerEvents = '';
          }
        });
    }

    // 5. Active Badges renderer
    function renderActiveBadges(formData) {
      if (!activeFiltersRow) return;
      activeFiltersRow.innerHTML = '';

      const types = formData.getAll('filter.p.product_type');
      const sizes = formData.getAll('filter.v.option.size');
      const colors = formData.getAll('filter.v.option.color');
      const availabilities = formData.getAll('filter.v.availability');
      const minPrice = formData.get('filter.v.price.gte');
      const maxPrice = formData.get('filter.v.price.lte');

      const badges = [];

      types.forEach(val => {
        badges.push({ name: 'filter.p.product_type', label: `TYPE: ${val.toUpperCase()}`, value: val });
      });

      sizes.forEach(val => {
        badges.push({ name: 'filter.v.option.size', label: `SIZE: ${val}`, value: val });
      });

      colors.forEach(val => {
        badges.push({ name: 'filter.v.option.color', label: `COLOR: ${val.toUpperCase()}`, value: val });
      });

      availabilities.forEach(val => {
        badges.push({ name: 'filter.v.availability', label: `AVAILABILITY: ${val === '1' ? 'IN STOCK' : 'OUT OF STOCK'}`, value: val });
      });

      if (minPrice && minPrice.trim() !== '') {
        badges.push({ name: 'filter.v.price.gte', label: `MIN PRICE: $${minPrice}`, value: minPrice });
      }

      if (maxPrice && maxPrice.trim() !== '') {
        badges.push({ name: 'filter.v.price.lte', label: `MAX PRICE: $${maxPrice}`, value: maxPrice });
      }

      badges.forEach(badgeData => {
        const badge = document.createElement('div');
        badge.className = 'jags-active-badge';
        badge.innerHTML = `
          <span class="font-mono text-xs">${badgeData.label}</span>
          <button type="button" class="jags-active-close-btn" aria-label="Remove filter ${badgeData.label}">&times;</button>
        `;

        badge.querySelector('.jags-active-close-btn').addEventListener('click', () => {
          if (filterForm) {
            const input = filterForm.querySelector(`input[name="${badgeData.name}"][value="${badgeData.value}"]`) ||
                          filterForm.querySelector(`input[name="${badgeData.name}"]`);
            if (input) {
              if (input.type === 'checkbox' || input.type === 'radio') {
                input.checked = false;
              } else {
                input.value = '';
              }
              applyStorefrontFilters();
            }
          }
        });

        activeFiltersRow.appendChild(badge);
      });
    }

    // Hook Form change event listener
    if (filterForm) {
      filterForm.addEventListener('change', (e) => {
        if (e.target.name === 'jags-collection-redirect') return;
        applyStorefrontFilters();
      });

      // Hook static collection redirection listener
      const collectionOptionFields = filterForm.querySelectorAll('.jags-collection-redirect-option');
      collectionOptionFields.forEach(option => {
        const input = option.querySelector('input');
        if (input) {
          input.addEventListener('change', () => {
            const targetUrl = option.getAttribute('data-url');
            if (targetUrl) {
              window.location.href = targetUrl;
            }
          });
        }
      });
    }

    // Initial load bindings
    initQuickAddSizes(grid);
    if (filterForm) {
      renderActiveBadges(new FormData(filterForm));
    }

    // 6. Luxury filter accordion toggle listeners
    const groupHeaders = document.querySelectorAll(`#JagsFilterDrawer-${sectionId} .jags-group-clickable-header`);
    groupHeaders.forEach((header) => {
      header.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const wrapper = header.closest('.jags-group-wrapper');
        if (wrapper) {
          const isCollapsed = wrapper.classList.toggle('is-collapsed');
          header.setAttribute('aria-expanded', !isCollapsed);
        }
      });
    });
  });
}
